import 'regenerator-runtime/runtime';
import { SagaIterator } from 'redux-saga';
import { AnyAction } from 'redux';
import { delay, race, select, take, takeLatest } from 'redux-saga/effects';
import {
  DoRequestResponse,
  GlobalActions,
  ProduktDeleted,
  StreckenTarifierungRequestEvents,
} from '@ps20-frontend/shop-integration-redux';
import { App } from '@ps20-frontend/frontend-models';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { IAppState } from '../appState';
import { TiergruppeState } from '../models/tiergruppe.model';
import { sharedDispatcher } from './sharedDispatcher';
import { snapshotManager } from './streckeSnapshotSaga';

/** The Strecken pricing of a product app failed (the app shows its own modal). */
export const PREISE_FAILED_EVENTS = [
  StreckenTarifierungRequestEvents.PREISE_REQUESTED_FAILED_TKV_V2,
  StreckenTarifierungRequestEvents.PREISE_REQUESTED_FAILED_THH_HUND,
  StreckenTarifierungRequestEvents.PREISE_REQUESTED_FAILED_THH_PFERD,
];

/** The pricing events of an app that report a success, e.g. after a retry in the modal. */
const PREISE_SUCCESSFUL_EVENTS: Record<string, string[]> = {
  [App.TKV_V2]: [
    StreckenTarifierungRequestEvents.PREISE_REQUESTED_SUCCESSFUL_MIT_INFO_TKV_V2,
    StreckenTarifierungRequestEvents.PREISE_REQUESTED_SUCCESSFUL_OHNE_INFO_TKV_V2,
  ],
  [App.THH_HUND]: [
    StreckenTarifierungRequestEvents.PREISE_REQUESTED_SUCCESSFUL_MIT_INFO_THH_HUND,
    StreckenTarifierungRequestEvents.PREISE_REQUESTED_SUCCESSFUL_OHNE_INFO_THH_HUND,
  ],
  [App.THH_PFERD]: [
    StreckenTarifierungRequestEvents.PREISE_REQUESTED_SUCCESSFUL_MIT_INFO_THH_PFERD,
    StreckenTarifierungRequestEvents.PREISE_REQUESTED_SUCCESSFUL_OHNE_INFO_THH_PFERD,
  ],
};

/**
 * The retry of exactly the failed offer succeeded. The pricing of another product of the group
 * may succeed while the modal is still open; that does not rescue the failed one.
 */
export const isRecoveryOf =
  (appId: App, angebotsId?: string) =>
  (action: AnyAction): boolean =>
    PREISE_SUCCESSFUL_EVENTS[appId]?.includes(action.type) &&
    action.doRequestAction?.payload?.angebotsId === angebotsId;

/** The product app has restored its snapshot some milliseconds after it navigated away. */
export const RESTORE_WAIT_MS = 3000;

export const isNavigationToDashboard = (action: AnyAction): boolean =>
  action.type === GlobalActions.NAVIGATION_REQUESTED &&
  action.payload?.appId === App.SHOP_DASHBOARD;

export const isHandledPricingFailure = (action: AnyAction): boolean =>
  PREISE_FAILED_EVENTS.includes(action.type) &&
  action.doRequestAction?.payload?.originEvent?.errorPolicy !== 'report';

export function* watchGroupFailure(): SagaIterator {
  yield takeLatest(isHandledPricingFailure, safeSaga(pricingFailedSaga, App.TGV));
}

/** Whether the failed offer was created for the group; other offers of the same app are not ours. */
const belongsToGroup = (state: IAppState, appId: App, angebotsId?: string): boolean => {
  const { angebotsIds, thhAngelegt } = state.tiergruppe;
  switch (appId) {
    case App.TKV_V2:
      return Object.values(angebotsIds).includes(angebotsId);
    case App.THH_HUND:
      return thhAngelegt.thhHund;
    case App.THH_PFERD:
      return thhAngelegt.thhPferd;
    default:
      return false;
  }
};

/** The offers of the group that no finished run has taken over into the dashboard yet. */
const offersNotTakenOver = (tiergruppe: TiergruppeState): ProduktDeleted['angebotsIds'] => [
  ...Object.entries(tiergruppe.angebotsIds)
    .filter(([cardId]) => !tiergruppe.uebernommen.includes(cardId))
    .map(([, angebotsId]) => ({ appId: App.TKV_V2, angebotsId })),
  ...(tiergruppe.thhAngelegt.thhHund && !tiergruppe.uebernommen.includes(App.THH_HUND)
    ? [{ appId: App.THH_HUND }]
    : []),
  ...(tiergruppe.thhAngelegt.thhPferd && !tiergruppe.uebernommen.includes(App.THH_PFERD)
    ? [{ appId: App.THH_PFERD }]
    : []),
];

const takenOverKeys = (tiergruppe: TiergruppeState): string[] => [
  ...Object.entries(tiergruppe.angebotsIds)
    .filter(([cardId]) => tiergruppe.uebernommen.includes(cardId))
    .map(([, angebotsId]) => angebotsId),
  ...(tiergruppe.thhAngelegt.thhHund && tiergruppe.uebernommen.includes(App.THH_HUND)
    ? [App.THH_HUND]
    : []),
  ...(tiergruppe.thhAngelegt.thhPferd && tiergruppe.uebernommen.includes(App.THH_PFERD)
    ? [App.THH_PFERD]
    : []),
];

export function abortGroup(navigateToDashboard: boolean, tiergruppe: TiergruppeState): void {
  restoreRepeatedRun(tiergruppe);
  removeAbortedOffers(navigateToDashboard, tiergruppe);
}

export function restoreRepeatedRun(tiergruppe: TiergruppeState): string[] {
  if (!(tiergruppe.uebernommen?.length > 0)) {
    return [];
  }
  const restoredKeys = snapshotManager.restoreChildren(takenOverKeys(tiergruppe));
  snapshotManager.restoreOwnState();
  return restoredKeys;
}

export function hasAbortedOffers(tiergruppe: TiergruppeState): boolean {
  return !(tiergruppe.uebernommen?.length > 0) || offersNotTakenOver(tiergruppe).length > 0;
}

export function removeAbortedOffers(
  navigateToDashboard: boolean,
  tiergruppe: TiergruppeState,
): void {
  const dispatcher = sharedDispatcher();
  const globalActions = new GlobalActions();
  if (tiergruppe.uebernommen?.length > 0) {
    const offers = offersNotTakenOver(tiergruppe);
    if (offers.length > 0) {
      dispatcher.dispatch(globalActions.produktDeleted(offers));
    }
  } else {
    dispatcher.dispatch(globalActions.produktDeleted([{ appId: App.TGV }]));
  }
  if (navigateToDashboard) {
    dispatcher.dispatch(globalActions.navigationRequested({ appId: App.SHOP_DASHBOARD }));
  }
}

/**
 * The product app handles the failure itself: a modal with a retry, or a modal that sends the
 * user back to the dashboard. A successful retry keeps everything as it is.
 */
export function* pricingFailedSaga(action: DoRequestResponse<unknown>): SagaIterator {
  const state: IAppState = yield select();
  const { appId, angebotsId } = action.doRequestAction?.payload ?? ({} as Record<string, never>);
  if (!belongsToGroup(state, appId, angebotsId)) {
    return;
  }
  const { gaveUp } = yield race({
    gaveUp: take(isNavigationToDashboard),
    recovered: take(isRecoveryOf(appId, angebotsId)),
  });
  if (!gaveUp) {
    return;
  }
  // The product app restores its snapshot right after it navigated; a restore after the deletion
  // would bring the state of the single THH stores back.
  yield race({
    restored: take(GlobalActions.RESTORE_STATE_SNAPSHOT_FINISHED),
    timeout: delay(RESTORE_WAIT_MS),
  });
  const { tiergruppe }: IAppState = yield select();
  abortGroup(false, tiergruppe);
}
